// ---------------------------------------------------------------------------
// Estratto VERBATIM da CRMMaster.tsx (Task #3873 — split meccanico).
// Nessuna modifica logica: solo header import e keyword export.
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useLegalForms } from '@easyflow/ui-frontend';
import { toErr } from '@easyflow/shared';
import {
  Users, FileText, FileSignature, Search, Trash2, Eye, Save, ChevronDown, Clock, AlertTriangle,
  CheckCircle, Check, MessageSquare, Paperclip, Target, Upload, ListChecks, Info, User
} from 'lucide-react';
import api from '@/lib/api';
import { CRM_BASE } from './types';
// Task #3875 — DTO risposta condivisi (alias per minimizzare il churn).
import type {
  CrmMasterPipelineEntry as PipelineEntry,
  CrmMasterLeadConversionData as LeadConversionData,
  CrmMasterEntryDetail as EntryDetailData,
  CrmMasterEntryHistoryItem as EntryHistoryItem,
  CrmMasterEntryNote as EntryNote,
  CrmMasterEntryAttachment as EntryAttachment,
  CrmMasterEntryActivity as EntryActivity,
} from '@easyflow/shared';
import {
  InputField, AddressField, SelectField, Badge, ModalWrapper
} from './shared';

export function NewEntryFiscalSection({ entry, setEntry }: { entry: Partial<PipelineEntry>; setEntry: React.Dispatch<React.SetStateAction<Partial<PipelineEntry>>> }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-muted/50 dark:bg-gray-700/30 rounded-xl border border-border dark:border-gray-600/50">
      <button type="button" onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-foreground dark:text-muted-foreground/60 hover:bg-muted dark:hover:bg-gray-700/50 rounded-xl transition-colors">
        <span className="flex items-center gap-2"><FileText className="h-4 w-4 text-amber-500" /> Dati Fiscali (opzionale)</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <InputField label="Ragione Sociale" value={entry.company_name} onChange={(v: string) => setEntry((e) => ({ ...e, company_name: v }))} uppercase />
            <InputField label="Partita IVA" value={entry.vat_number} onChange={(v: string) => setEntry((e) => ({ ...e, vat_number: v }))} placeholder="IT12345678901" />
            <InputField label="Codice Fiscale" value={entry.fiscal_code} onChange={(v: string) => setEntry((e) => ({ ...e, fiscal_code: v }))} />
            <InputField label="Codice SDI" value={entry.sdi_code} onChange={(v: string) => setEntry((e) => ({ ...e, sdi_code: v }))} placeholder="0000000" />
            <InputField label="PEC" value={entry.pec} onChange={(v: string) => setEntry((e) => ({ ...e, pec: v }))} type="email" />
            <AddressField label="Cerca indirizzo" value={entry.legal_address ?? undefined} onSelect={(a) => setEntry((e) => ({ ...e, legal_address: a.address ?? '', ...(a.city !== undefined ? { legal_city: a.city } : {}), ...(a.province !== undefined ? { legal_province: a.province } : {}), ...(a.postal_code !== undefined ? { legal_zip: a.postal_code } : {}) }))} />
            <InputField label="Indirizzo Legale" value={entry.legal_address} onChange={(v: string) => setEntry((e) => ({ ...e, legal_address: v }))} />
            <InputField label="Città" value={entry.legal_city} onChange={(v: string) => setEntry((e) => ({ ...e, legal_city: v }))} />
            <div className="grid grid-cols-2 gap-2">
              <InputField label="Provincia" value={entry.legal_province} onChange={(v: string) => setEntry((e) => ({ ...e, legal_province: v }))} placeholder="MI" />
              <InputField label="CAP" value={entry.legal_zip} onChange={(v: string) => setEntry((e) => ({ ...e, legal_zip: v }))} placeholder="20100" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function FiscalFieldsSection({ entry, setEditEntry }: { entry: PipelineEntry; setEditEntry: React.Dispatch<React.SetStateAction<PipelineEntry | null>> }) {
  const [expanded, setExpanded] = useState(
    !!(entry.company_name || entry.vat_number || entry.fiscal_code || entry.sdi_code || entry.pec || entry.legal_address)
  );

  return (
    <div className="bg-muted/50 dark:bg-gray-700/30 rounded-xl border border-border dark:border-gray-600/50">
      <button onClick={() => setExpanded(!expanded)} type="button"
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted dark:hover:bg-gray-700/50 transition-colors rounded-xl">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-amber-500" />
          <span className="text-foreground dark:text-white font-semibold text-sm">Dati Fiscali</span>
          {(entry.vat_number || entry.fiscal_code) && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-500/20 text-green-400 border border-green-500/30">
              Compilati
            </span>
          )}
        </div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground/70 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-border dark:border-gray-600/50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <InputField label="Ragione Sociale" value={entry.company_name || ''} onChange={(v: string) => setEditEntry((e) => ({ ...(e as PipelineEntry), company_name: v }))} className="md:col-span-2" uppercase />
            <InputField label="Partita IVA" value={entry.vat_number || ''} onChange={(v: string) => setEditEntry((e) => ({ ...(e as PipelineEntry), vat_number: v }))} placeholder="IT12345678901" />
            <InputField label="Codice Fiscale" value={entry.fiscal_code || ''} onChange={(v: string) => setEditEntry((e) => ({ ...(e as PipelineEntry), fiscal_code: v }))} />
            <InputField label="Codice SDI" value={entry.sdi_code || ''} onChange={(v: string) => setEditEntry((e) => ({ ...(e as PipelineEntry), sdi_code: v }))} placeholder="0000000" />
            <InputField label="PEC" value={entry.pec || ''} onChange={(v: string) => setEditEntry((e) => ({ ...(e as PipelineEntry), pec: v }))} type="email" placeholder="pec@pec.it" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <AddressField value={entry.legal_address || ''} className="md:col-span-2" onSelect={(a) => setEditEntry((e) => ({ ...(e as PipelineEntry), legal_address: a.address ?? '', ...(a.city !== undefined ? { legal_city: a.city } : {}), ...(a.province !== undefined ? { legal_province: a.province } : {}), ...(a.postal_code !== undefined ? { legal_zip: a.postal_code } : {}) }))} />
            <InputField label="Indirizzo" value={entry.legal_address || ''} onChange={(v: string) => setEditEntry((e) => ({ ...(e as PipelineEntry), legal_address: v }))} className="md:col-span-2" />
            <InputField label="Città" value={entry.legal_city || ''} onChange={(v: string) => setEditEntry((e) => ({ ...(e as PipelineEntry), legal_city: v }))} />
            <div className="grid grid-cols-2 gap-2">
              <InputField label="Prov." value={entry.legal_province || ''} onChange={(v: string) => setEditEntry((e) => ({ ...(e as PipelineEntry), legal_province: v }))} placeholder="MI" />
              <InputField label="CAP" value={entry.legal_zip || ''} onChange={(v: string) => setEditEntry((e) => ({ ...(e as PipelineEntry), legal_zip: v }))} placeholder="20100" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export type CustomerMatchKey = 'vat' | 'cf' | 'email' | 'phone';
export interface CustomerMatchRow {
  id: string;
  company_name: string | null;
  contact_name: string | null;
  vat_number: string | null;
  fiscal_code: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  match_key: CustomerMatchKey;
}
export interface CustomerMatchResult {
  status: 'auto' | 'da_verificare' | 'none';
  auto: CustomerMatchRow | null;
  candidates: CustomerMatchRow[];
}
export const MATCH_KEY_LABEL: Record<CustomerMatchKey, string> = {
  vat: 'P.IVA', cf: 'Cod. Fiscale', email: 'Email', phone: 'Telefono',
};

export function LeadConversionWizard({ open, onClose, entryId, onConverted }: { open: boolean; onClose: () => void; entryId: string; onConverted: () => void }) {
  const { toast } = useToast();
  const { legalForms } = useLegalForms();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [leadData, setLeadData] = useState<LeadConversionData | null>(null);
  const [dupWarning, setDupWarning] = useState<string | null>(null);
  const [matchResult, setMatchResult] = useState<CustomerMatchResult | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerMatchRow | null>(null);
  const [matchChecking, setMatchChecking] = useState(false);
  const [matchChecked, setMatchChecked] = useState(false);
  const [form, setForm] = useState({
    company_name: '', legal_form: '', vat_number: '', fiscal_code: '',
    sdi_code: '', pec: '', legal_address: '', legal_city: '', legal_province: '', legal_zip: '',
    contact_first_name: '', contact_last_name: '', contact_email: '', contact_phone: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open || !entryId) return;
    setStep(1);
    setErrors({});
    setDupWarning(null);
    setMatchResult(null);
    setSelectedCustomer(null);
    setMatchChecked(false);
    setLoading(true);
    void api.get<LeadConversionData>(`${CRM_BASE}/pipeline/entries/${entryId}/conversion-data`).then((res) => {
      if (res.success && res.data) {
        const d = res.data;
        setLeadData(d);
        setForm({
          company_name: d.company_name || d.contact_name || '',
          legal_form: d.legal_form || '',
          vat_number: d.vat_number || '',
          fiscal_code: d.fiscal_code || '',
          sdi_code: d.sdi_code || '',
          pec: d.pec || '',
          legal_address: d.legal_address || '',
          legal_city: d.legal_city || '',
          legal_province: d.legal_province || '',
          legal_zip: d.legal_zip || '',
          contact_first_name: d.contact_first_name || (d.contact_name || '').split(' ')[0] || '',
          contact_last_name: d.contact_last_name || (d.contact_name || '').split(' ').slice(1).join(' ') || '',
          contact_email: d.contact_email || '',
          contact_phone: d.contact_phone || '',
        });
      }
    }).finally(() => setLoading(false));
  }, [open, entryId]);

  const requiredFields = [
    { key: 'company_name', label: 'Ragione Sociale' },
    { key: 'legal_form', label: 'Forma Giuridica' },
    { key: 'vat_number', label: 'Partita IVA' },
    { key: 'fiscal_code', label: 'Codice Fiscale' },
    { key: 'sdi_code', label: 'Codice SDI' },
    { key: 'pec', label: 'PEC' },
    { key: 'legal_address', label: 'Indirizzo' },
    { key: 'legal_city', label: 'Città' },
    { key: 'legal_province', label: 'Provincia' },
    { key: 'legal_zip', label: 'CAP' },
  ];

  const filledCount = requiredFields.filter(f => { const v = form[f.key as keyof typeof form]; return typeof v === 'string' && v.trim(); }).length;
  const totalRequired = requiredFields.length;

  const checkMatchClient = async () => {
    if (!form.vat_number.trim() && !form.fiscal_code.trim()) return;
    setMatchChecking(true);
    try {
      const params = new URLSearchParams();
      if (form.vat_number.trim()) params.set('vat_number', form.vat_number.trim());
      if (form.fiscal_code.trim()) params.set('fiscal_code', form.fiscal_code.trim());
      const res = await api.get(`${CRM_BASE}/pipeline/entries/${entryId}/match-client?${params.toString()}`);
      if (res.success && res.data) {
        const result = res.data as CustomerMatchResult;
        setMatchResult(result);
        // Match forte univoco → preseleziona il cliente per il collegamento (auto-link).
        setSelectedCustomer(result.status === 'auto' ? result.auto : null);
      } else {
        setMatchResult(null);
        setSelectedCustomer(null);
      }
    } catch {
      setMatchResult(null);
      setSelectedCustomer(null);
    } finally {
      setMatchChecking(false);
      setMatchChecked(true);
    }
  };

  const selectCustomer = (c: CustomerMatchRow) => {
    setSelectedCustomer(c);
    toast({ title: 'Cliente collegato', description: `La conversione collegherà il lead a ${c.company_name || c.contact_name || 'questo cliente'} senza creare un duplicato.` });
  };
  const clearSelectedCustomer = () => setSelectedCustomer(null);

  const validateStep = (s: number): boolean => {
    const newErrors: Record<string, string> = {};
    if (s === 1) {
      if (!form.company_name.trim()) newErrors.company_name = 'Ragione Sociale obbligatoria';
      if (!form.legal_form) newErrors.legal_form = 'Forma Giuridica obbligatoria';
      if (!form.contact_first_name.trim()) newErrors.contact_first_name = 'Nome referente obbligatorio';
    }
    // In modalità collegamento a cliente esistente NON creiamo l'anagrafica:
    // i campi fiscali non sono richiesti.
    if (s === 2 && !selectedCustomer) {
      requiredFields.forEach(f => {
        const v = form[f.key as keyof typeof form]; if (typeof v !== 'string' || !v.trim()) newErrors[f.key] = `${f.label} obbligatorio`;
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep(step)) return;
    if (step === 1 && !matchChecked && (form.vat_number.trim() || form.fiscal_code.trim())) {
      await checkMatchClient();
    }
    setStep(s => Math.min(s + 1, 3));
  };
  const handleBack = () => setStep(s => Math.max(s - 1, 1));

  const handleConvert = async () => {
    if (!validateStep(step)) return;
    setSaving(true);
    try {
      // Collegamento (customer_id) vs creazione (anagrafica piatta dal form).
      const payload = selectedCustomer ? { customer_id: selectedCustomer.id } : form;
      const res = await api.post(`${CRM_BASE}/pipeline/entries/${entryId}/convert-to-client`, payload);
      if (res.success) {
        const confirmedName = selectedCustomer
          ? (selectedCustomer.company_name || selectedCustomer.contact_name || 'Il cliente')
          : form.company_name;
        toast({ title: 'Cliente confermato', description: `${confirmedName} è ora un cliente attivo.` });
        onConverted();
        onClose();
      } else {
        const msg = (res as { error?: string }).error || 'Errore conversione';
        if (msg.includes('P.IVA')) setDupWarning(msg);
        else toast({ title: 'Errore', description: msg, variant: 'destructive' });
      }
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({ title: 'Errore', description: __err?.message || 'Errore conversione', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const update = (field: string) => (value: string) => {
    setForm(f => ({ ...f, [field]: value }));
    if (field === 'vat_number' || field === 'fiscal_code') {
      setDupWarning(null);
      setMatchChecked(false);
      setMatchResult(null);
      setSelectedCustomer(null);
    }
  };

  if (!open) return null;
  const alreadyClient = leadData?.conversion_data_saved;
  const legalFormLabel = legalForms.find(lf => lf.code === form.legal_form)?.label || form.legal_form || '—';

  return (
    <ModalWrapper open={open} onClose={onClose} title="Verifica e Conferma Cliente" wide>
      {loading ? (
        <div className="text-muted-foreground dark:text-muted-foreground/70 text-center py-8">Caricamento dati lead...</div>
      ) : alreadyClient ? (
        <div className="text-center py-8">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
          <p className="text-foreground dark:text-white font-semibold text-lg mb-1">Cliente già confermato</p>
          <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Questo lead è già stato verificato e confermato come cliente. Puoi procedere con il setup pagamento.</p>
          <button onClick={onClose} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium">Chiudi</button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            {[
              { n: 1, label: 'Anagrafica', icon: User },
              { n: 2, label: 'Dati Fiscali', icon: FileText },
              { n: 3, label: 'Riepilogo', icon: Check },
            ].map((s, idx) => (
              <React.Fragment key={s.n}>
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    step > s.n ? 'bg-green-500 text-white' : step === s.n ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-muted-foreground dark:text-muted-foreground/70'
                  }`}>
                    {step > s.n ? <Check className="h-4 w-4" /> : <s.icon className="h-4 w-4" />}
                  </div>
                  <span className={`text-sm font-medium hidden sm:inline ${step === s.n ? 'text-foreground dark:text-white' : 'text-muted-foreground dark:text-muted-foreground/70'}`}>{s.label}</span>
                </div>
                {idx < 2 && <div className={`flex-1 h-0.5 mx-3 ${step > s.n ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} />}
              </React.Fragment>
            ))}
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-lg px-4 py-2.5 flex items-center gap-2">
            <Target className="h-4 w-4 text-blue-500 flex-shrink-0" />
            <span className="text-sm text-blue-700 dark:text-blue-300">
              Completamento: <strong>{filledCount}/{totalRequired}</strong> campi obbligatori
            </span>
            <div className="flex-1 h-1.5 bg-blue-200 dark:bg-blue-800 rounded-full ml-2">
              <div className="h-1.5 bg-blue-500 rounded-full transition-all" style={{ width: `${(filledCount / totalRequired) * 100}%` }} />
            </div>
          </div>

          {dupWarning && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg px-4 py-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700 dark:text-red-300">{dupWarning}</span>
            </div>
          )}

          {matchResult && matchResult.status !== 'none' && step >= 2 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg px-4 py-3">
              <div className="flex items-start gap-2">
                <Search className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                    {matchResult.status === 'auto'
                      ? 'Cliente esistente corrispondente'
                      : 'Possibili clienti esistenti — verifica e scegli'}
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                    Collega il lead a un cliente esistente per evitare duplicati, oppure prosegui per crearne uno nuovo.
                  </p>
                  <div className="mt-2 space-y-1.5">
                    {matchResult.candidates.map(c => {
                      const isSel = selectedCustomer?.id === c.id;
                      return (
                        <div key={c.id} className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${isSel ? 'border-green-400 bg-green-50 dark:border-green-700 dark:bg-green-900/20' : 'border-amber-200 bg-white dark:border-amber-800/50 dark:bg-gray-800'}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground dark:text-white truncate">{c.company_name || c.contact_name || '—'}</p>
                            <p className="text-xs text-muted-foreground dark:text-muted-foreground/70 truncate">
                              P.IVA: {c.vat_number || '—'} · CF: {c.fiscal_code || '—'} · <span className="uppercase">{MATCH_KEY_LABEL[c.match_key]}</span>
                            </p>
                          </div>
                          {isSel ? (
                            <button onClick={clearSelectedCustomer} className="flex-shrink-0 text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded font-medium flex items-center gap-1">
                              <Check className="h-3.5 w-3.5" /> Collegato
                            </button>
                          ) : (
                            <button onClick={() => selectCustomer(c)} className="flex-shrink-0 text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 rounded font-medium">
                              Collega
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {selectedCustomer && (
                    <button onClick={clearSelectedCustomer} className="mt-2 text-xs text-amber-700 dark:text-amber-300 hover:underline">
                      Annulla collegamento e crea un nuovo cliente
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {matchChecked && (!matchResult || matchResult.status === 'none') && step >= 2 && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-lg px-4 py-2 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span className="text-sm text-green-700 dark:text-green-300">Nessun cliente esistente trovato con questi dati fiscali. Verrà creato un nuovo cliente.</span>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <InputField label="Ragione Sociale *" value={form.company_name} onChange={update('company_name')} uppercase />
                  {errors.company_name && <p className="text-xs text-red-500 mt-0.5">{errors.company_name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-muted-foreground/60 mb-1">Forma Giuridica *</label>
                  <select
                    value={form.legal_form}
                    onChange={e => update('legal_form')(e.target.value)}
                    className="w-full rounded-lg border border-border dark:border-gray-600 bg-white dark:bg-gray-800 text-foreground dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-inset focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">— Seleziona —</option>
                    {legalForms.map(lf => (
                      <option key={lf.code} value={lf.code}>{lf.label}</option>
                    ))}
                  </select>
                  {errors.legal_form && <p className="text-xs text-red-500 mt-0.5">{errors.legal_form}</p>}
                </div>
                <div>
                  <InputField label="Nome Referente *" value={form.contact_first_name} onChange={update('contact_first_name')} uppercase />
                  {errors.contact_first_name && <p className="text-xs text-red-500 mt-0.5">{errors.contact_first_name}</p>}
                </div>
                <InputField label="Cognome Referente" value={form.contact_last_name} onChange={update('contact_last_name')} uppercase />
                <InputField label="Email" value={form.contact_email} onChange={update('contact_email')} type="email" />
                <InputField label="Telefono" value={form.contact_phone} onChange={update('contact_phone')} />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <InputField label="Partita IVA *" value={form.vat_number} onChange={update('vat_number')} placeholder="IT12345678901" />
                  {errors.vat_number && <p className="text-xs text-red-500 mt-0.5">{errors.vat_number}</p>}
                </div>
                <div>
                  <InputField label="Codice Fiscale *" value={form.fiscal_code} onChange={update('fiscal_code')} />
                  {errors.fiscal_code && <p className="text-xs text-red-500 mt-0.5">{errors.fiscal_code}</p>}
                </div>
                <div>
                  <InputField label="Codice SDI *" value={form.sdi_code} onChange={update('sdi_code')} placeholder="0000000" />
                  {errors.sdi_code && <p className="text-xs text-red-500 mt-0.5">{errors.sdi_code}</p>}
                </div>
                <div>
                  <InputField label="PEC *" value={form.pec} onChange={update('pec')} type="email" placeholder="pec@pec.it" />
                  {errors.pec && <p className="text-xs text-red-500 mt-0.5">{errors.pec}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="md:col-span-2">
                  <AddressField label="Cerca indirizzo" value={form.legal_address} onSelect={(a) => setForm((f) => ({ ...f, legal_address: a.address ?? '', ...(a.city !== undefined ? { legal_city: a.city } : {}), ...(a.province !== undefined ? { legal_province: a.province } : {}), ...(a.postal_code !== undefined ? { legal_zip: a.postal_code } : {}) }))} />
                  <InputField label="Indirizzo *" value={form.legal_address} onChange={update('legal_address')} />
                  {errors.legal_address && <p className="text-xs text-red-500 mt-0.5">{errors.legal_address}</p>}
                </div>
                <div>
                  <InputField label="Città *" value={form.legal_city} onChange={update('legal_city')} />
                  {errors.legal_city && <p className="text-xs text-red-500 mt-0.5">{errors.legal_city}</p>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <InputField label="Prov. *" value={form.legal_province} onChange={update('legal_province')} placeholder="MI" />
                    {errors.legal_province && <p className="text-xs text-red-500 mt-0.5">{errors.legal_province}</p>}
                  </div>
                  <div>
                    <InputField label="CAP *" value={form.legal_zip} onChange={update('legal_zip')} placeholder="20100" />
                    {errors.legal_zip && <p className="text-xs text-red-500 mt-0.5">{errors.legal_zip}</p>}
                  </div>
                </div>
              </div>
              {!matchChecked && (form.vat_number.trim() || form.fiscal_code.trim()) && (
                <button onClick={checkMatchClient} disabled={matchChecking}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1">
                  <Search className="h-3.5 w-3.5" />
                  {matchChecking ? 'Ricerca in corso...' : 'Verifica cliente esistente'}
                </button>
              )}
            </div>
          )}

          {step === 3 && selectedCustomer && (
            <div className="space-y-3">
              <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-4 space-y-3">
                <h4 className="text-foreground dark:text-white font-semibold text-sm flex items-center gap-2">
                  <User className="h-4 w-4 text-green-500" /> Collegamento a cliente esistente
                </h4>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  <span className="text-muted-foreground dark:text-muted-foreground/70">Cliente:</span><span className="text-foreground dark:text-white font-medium">{selectedCustomer.company_name || selectedCustomer.contact_name || '—'}</span>
                  <span className="text-muted-foreground dark:text-muted-foreground/70">P.IVA:</span><span className="text-foreground dark:text-white font-mono">{selectedCustomer.vat_number || '—'}</span>
                  <span className="text-muted-foreground dark:text-muted-foreground/70">Codice Fiscale:</span><span className="text-foreground dark:text-white font-mono">{selectedCustomer.fiscal_code || '—'}</span>
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-lg px-4 py-3">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-700 dark:text-green-300">
                    <p className="font-medium">Conferma collegamento</p>
                    <p>Il lead verrà collegato a questo cliente esistente, senza creare duplicati. Dopo la conferma potrai procedere con il setup pagamento.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && !selectedCustomer && (
            <div className="space-y-3">
              <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-4 space-y-3">
                <h4 className="text-foreground dark:text-white font-semibold text-sm flex items-center gap-2">
                  <User className="h-4 w-4 text-blue-500" /> Anagrafica
                </h4>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  <span className="text-muted-foreground dark:text-muted-foreground/70">Ragione Sociale:</span><span className="text-foreground dark:text-white font-medium">{form.company_name}</span>
                  <span className="text-muted-foreground dark:text-muted-foreground/70">Forma Giuridica:</span><span className="text-foreground dark:text-white">{legalFormLabel}</span>
                  <span className="text-muted-foreground dark:text-muted-foreground/70">Referente:</span><span className="text-foreground dark:text-white">{form.contact_first_name} {form.contact_last_name}</span>
                  <span className="text-muted-foreground dark:text-muted-foreground/70">Email:</span><span className="text-foreground dark:text-white">{form.contact_email}</span>
                  <span className="text-muted-foreground dark:text-muted-foreground/70">Telefono:</span><span className="text-foreground dark:text-white">{form.contact_phone}</span>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-4 space-y-3">
                <h4 className="text-foreground dark:text-white font-semibold text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4 text-amber-500" /> Dati Fiscali
                </h4>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  <span className="text-muted-foreground dark:text-muted-foreground/70">P.IVA:</span><span className="text-foreground dark:text-white font-mono">{form.vat_number}</span>
                  <span className="text-muted-foreground dark:text-muted-foreground/70">Codice Fiscale:</span><span className="text-foreground dark:text-white font-mono">{form.fiscal_code}</span>
                  <span className="text-muted-foreground dark:text-muted-foreground/70">SDI:</span><span className="text-foreground dark:text-white font-mono">{form.sdi_code}</span>
                  <span className="text-muted-foreground dark:text-muted-foreground/70">PEC:</span><span className="text-foreground dark:text-white">{form.pec}</span>
                  <span className="text-muted-foreground dark:text-muted-foreground/70">Indirizzo:</span><span className="text-foreground dark:text-white">{form.legal_address}, {form.legal_city} ({form.legal_province}) {form.legal_zip}</span>
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-lg px-4 py-3">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-700 dark:text-green-300">
                    <p className="font-medium">Conferma dati cliente</p>
                    <p>Verrà creato un nuovo cliente con questi dati anagrafici e fiscali. Dopo la conferma potrai procedere con il setup pagamento.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between pt-2 border-t border-border dark:border-gray-700">
            <button onClick={step > 1 ? handleBack : onClose}
              className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white font-medium text-sm">
              {step > 1 ? '← Indietro' : 'Annulla'}
            </button>
            {step < 3 ? (
              <button onClick={handleNext}
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-medium text-sm">
                Avanti →
              </button>
            ) : (
              <button onClick={handleConvert} disabled={saving}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg font-medium text-sm disabled:opacity-50">
                <Check className="h-4 w-4" />{saving ? 'Salvataggio...' : 'Conferma Cliente'}
              </button>
            )}
          </div>
        </div>
      )}
    </ModalWrapper>
  );
}

export function PipelineEntryDetail({ entry, setEditEntry, onSave, onClose, onConvert, onDelete }: { entry: PipelineEntry; setEditEntry: React.Dispatch<React.SetStateAction<PipelineEntry | null>>; onSave: () => void; onClose: () => void; onConvert?: () => void; onDelete?: () => void }) {
  const [activeTab, setActiveTab] = useState('info');
  const [detail, setDetail] = useState<EntryDetailData | null>(null);
  const [history, setHistory] = useState<EntryHistoryItem[]>([]);
  const [notes, setNotes] = useState<EntryNote[]>([]);
  const [attachments, setAttachments] = useState<EntryAttachment[]>([]);
  const [activities, setActivities] = useState<EntryActivity[]>([]);
  const [newNote, setNewNote] = useState('');
  const [newActivity, setNewActivity] = useState({ title: '', type: 'task', due_date: '', description: '' });
  const [uploading, setUploading] = useState(false);
  const [loadingTab, setLoadingTab] = useState(false);
  // Task #3875: sul wire `tags` è string[] (colonna text[]); la UI edita una CSV.
  // Draft locale per non normalizzare mentre l'utente digita.
  const [tagsInput, setTagsInput] = useState<string | null>(null);

  useEffect(() => {
    // Task #3875: il draft CSV dei tags è per-entry — al cambio entry va
    // azzerato, altrimenti la CSV della entry precedente resta visibile e
    // può essere salvata sulla nuova (cross-contaminazione).
    setTagsInput(null);
    const load = async () => {
      try {
        const [detailRes, historyRes] = await Promise.all([
          api.get<EntryDetailData>(`${CRM_BASE}/pipeline/entries/${entry.id}/detail`),
          api.get<EntryHistoryItem[] | { history?: EntryHistoryItem[] }>(`${CRM_BASE}/pipeline/entries/${entry.id}/history`),
        ]);
        if (detailRes.success && detailRes.data) {
          setDetail(detailRes.data);
          const d = detailRes.data;
          setEditEntry((e) => ({ ...(e as PipelineEntry),
            ...e,
            contact_name: d.contact_name || e?.contact_name || '',
            contact_email: d.contact_email || e?.contact_email || '',
            contact_phone: d.contact_phone || e?.contact_phone || '',
            lead_source: d.lead_source || e?.lead_source || '',
            priority: d.priority || e?.priority || '',
            tags: d.tags ?? e?.tags ?? null,
            next_followup_at: d.next_followup_at || e?.next_followup_at || '',
            company_name: d.company_name || e?.company_name || '',
            vat_number: d.vat_number || e?.vat_number || '',
            fiscal_code: d.fiscal_code || e?.fiscal_code || '',
            sdi_code: d.sdi_code || e?.sdi_code || '',
            pec: d.pec || e?.pec || '',
            legal_address: d.legal_address || e?.legal_address || '',
            legal_city: d.legal_city || e?.legal_city || '',
            legal_province: d.legal_province || e?.legal_province || '',
            legal_zip: d.legal_zip || e?.legal_zip || '',
            customer_id: d.customer_id || e?.customer_id || null,
          }));
        }
        if (historyRes.success && historyRes.data) setHistory(Array.isArray(historyRes.data) ? historyRes.data : (historyRes.data).history || []);
      } catch {}
    };
    void load();
  }, [entry.id]);

  const loadNotes = useCallback(async () => {
    setLoadingTab(true);
    try {
      const res = await api.get<EntryNote[] | { notes?: EntryNote[] }>(`${CRM_BASE}/pipeline/entries/${entry.id}/notes`);
      if (res.success && res.data) setNotes(Array.isArray(res.data) ? res.data : (res.data).notes || []);
    } catch {} finally { setLoadingTab(false); }
  }, [entry.id]);

  const loadAttachments = useCallback(async () => {
    setLoadingTab(true);
    try {
      const res = await api.get<EntryAttachment[] | { attachments?: EntryAttachment[] }>(`${CRM_BASE}/pipeline/entries/${entry.id}/attachments`);
      if (res.success && res.data) setAttachments(Array.isArray(res.data) ? res.data : (res.data).attachments || []);
    } catch {} finally { setLoadingTab(false); }
  }, [entry.id]);

  const loadActivities = useCallback(async () => {
    setLoadingTab(true);
    try {
      const res = await api.get<EntryActivity[] | { activities?: EntryActivity[] }>(`${CRM_BASE}/pipeline/entries/${entry.id}/activities`);
      if (res.success && res.data) setActivities(Array.isArray(res.data) ? res.data : (res.data).activities || []);
    } catch {} finally { setLoadingTab(false); }
  }, [entry.id]);

  useEffect(() => {
    if (activeTab === 'notes') void loadNotes();
    if (activeTab === 'attachments') void loadAttachments();
    if (activeTab === 'activities') void loadActivities();
  }, [activeTab, loadNotes, loadAttachments, loadActivities]);

  const addNote = async () => {
    if (!newNote.trim()) return;
    await api.post(`${CRM_BASE}/pipeline/entries/${entry.id}/notes`, { content: newNote });
    setNewNote('');
    void loadNotes();
  };

  const deleteNote = async (noteId: string) => {
    await api.delete(`${CRM_BASE}/pipeline/notes/${noteId}`);
    void loadNotes();
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`${CRM_BASE}/pipeline/entries/${entry.id}/attachments`, formData);
      void loadAttachments();
    } finally { setUploading(false); }
  };

  const deleteAttachment = async (id: string) => {
    await api.delete(`${CRM_BASE}/pipeline/attachments/${id}`);
    void loadAttachments();
  };

  const addActivity = async () => {
    if (!newActivity.title.trim()) return;
    await api.post(`${CRM_BASE}/pipeline/entries/${entry.id}/activities`, newActivity);
    setNewActivity({ title: '', type: 'task', due_date: '', description: '' });
    void loadActivities();
  };

  const toggleActivity = async (id: string) => {
    await api.put(`${CRM_BASE}/pipeline/activities/${id}/toggle`, {});
    void loadActivities();
  };

  const deleteActivity = async (id: string) => {
    await api.delete(`${CRM_BASE}/pipeline/activities/${id}`);
    void loadActivities();
  };

  const tabs = [
    { key: 'info', label: 'Info', icon: Target },
    { key: 'notes', label: 'Note', icon: MessageSquare },
    { key: 'attachments', label: 'Allegati', icon: Paperclip },
    { key: 'activities', label: 'Attività', icon: ListChecks },
    { key: 'quotes', label: 'Preventivi', icon: FileText },
    { key: 'contracts', label: 'Contratti', icon: FileSignature },
    { key: 'history', label: 'Storico', icon: Clock },
  ];

  return (
    <div className="space-y-4">
      <p className="text-foreground dark:text-white font-medium text-lg">{entry.tenant_name}</p>
      <div className="flex gap-1 border-b border-border dark:border-gray-700 overflow-x-auto pb-px">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
              activeTab === t.key
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 bg-blue-50/50 dark:bg-blue-900/20'
                : 'text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white'
            }`}>
            <t.icon className="h-3.5 w-3.5" />{t.label}
          </button>
        ))}
      </div>

      {activeTab === 'info' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField label="Nome Contatto" value={entry.contact_name || ''} onChange={(v: string) => setEditEntry((e) => ({ ...(e as PipelineEntry), contact_name: v }))} uppercase />
            <InputField label="Email Contatto" value={entry.contact_email || ''} onChange={(v: string) => setEditEntry((e) => ({ ...(e as PipelineEntry), contact_email: v }))} />
            <InputField label="Telefono Contatto" value={entry.contact_phone || ''} onChange={(v: string) => setEditEntry((e) => ({ ...(e as PipelineEntry), contact_phone: v }))} />
            <SelectField label="Fonte Lead" value={entry.lead_source || ''} onChange={(v: string) => setEditEntry((e) => ({ ...(e as PipelineEntry), lead_source: v }))}
              options={[{ value: '', label: 'Nessuna' }, { value: 'web_form', label: 'Web Form' }, { value: 'phone', label: 'Telefono' }, { value: 'referral', label: 'Referral' }, { value: 'email', label: 'Email' }, { value: 'social', label: 'Social' }, { value: 'event', label: 'Evento' }, { value: 'other', label: 'Altro' }]} />
            <SelectField label="Priorità" value={entry.priority || ''} onChange={(v: string) => setEditEntry((e) => ({ ...(e as PipelineEntry), priority: v }))}
              options={[{ value: '', label: 'Nessuna' }, { value: 'high', label: 'Alta' }, { value: 'medium', label: 'Media' }, { value: 'low', label: 'Bassa' }]} />
            <InputField label="Tags (separati da virgola)" value={tagsInput ?? (Array.isArray(entry.tags) ? entry.tags.join(', ') : '')}
              onChange={(v: string) => { setTagsInput(v); setEditEntry((e) => ({ ...(e as PipelineEntry), tags: v.split(',').map((s) => s.trim()).filter(Boolean) })); }} />
            <div>
              <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Prossimo Follow-up</label>
              <input type="datetime-local" value={entry.next_followup_at ? String(entry.next_followup_at).slice(0, 16) : ''}
                onChange={(e) => setEditEntry((en) => ({ ...(en as PipelineEntry), next_followup_at: e.target.value }))}
                className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm" />
            </div>
            <InputField label="Valore Opportunità (€)" value={entry.opportunity_value} onChange={(v: string) => setEditEntry((e) => ({ ...(e as PipelineEntry), opportunity_value: v }))} type="number" />
            <InputField label="Assegnato a" value={entry.assigned_to || ''} onChange={(v: string) => setEditEntry((e) => ({ ...(e as PipelineEntry), assigned_to: v }))} />
          </div>
          <FiscalFieldsSection entry={entry} setEditEntry={setEditEntry} />
          <div>
            <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Motivo Perdita</label>
            <textarea value={entry.loss_reason || ''} onChange={(e) => setEditEntry((en) => ({ ...(en as PipelineEntry), loss_reason: e.target.value }))}
              className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm h-16 resize-none" />
          </div>
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Aggiungi una nota..."
              className="flex-1 bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm h-16 resize-none" />
            <button onClick={addNote} className="bg-blue-600 hover:bg-blue-700 text-white px-4 rounded-lg font-medium text-sm self-end h-10">Aggiungi</button>
          </div>
          {loadingTab ? <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Caricamento...</p> : notes.length === 0 ? (
            <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm text-center py-4">Nessuna nota</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {notes.map((n) => (
                <div key={n.id} className="bg-muted dark:bg-gray-700/50 rounded-lg p-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-foreground dark:text-white text-sm flex-1 whitespace-pre-wrap">{n.content}</p>
                    <button onClick={() => deleteNote(n.id)} className="text-muted-foreground/70 hover:text-red-400 p-1 flex-shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="text-xs text-muted-foreground dark:text-muted-foreground/70 mt-1">
                    {n.created_by && <span>{n.created_by} · </span>}
                    {n.created_at && new Date(n.created_at).toLocaleString('it-IT')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'attachments' && (
        <div className="space-y-3">
          <div>
            <label className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium text-sm cursor-pointer w-fit">
              <Upload className="h-4 w-4" />{uploading ? 'Caricamento...' : 'Carica File'}
              <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
            </label>
          </div>
          {loadingTab ? <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Caricamento...</p> : attachments.length === 0 ? (
            <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm text-center py-4">Nessun allegato</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {attachments.map((a) => (
                <div key={a.id} className="bg-muted dark:bg-gray-700/50 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground dark:text-white text-sm font-medium truncate">{a.filename}</p>
                    <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">
                      {a.size_bytes ? `${(Number(a.size_bytes) / 1024).toFixed(1)} KB` : ''}
                      {a.uploaded_by ? ` · ${a.uploaded_by}` : ''}
                      {a.created_at ? ` · ${new Date(a.created_at).toLocaleDateString('it-IT')}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <a href={`/api${CRM_BASE}/pipeline/attachments/${a.id}/download`} target="_blank" rel="noreferrer"
                      className="text-blue-500 hover:text-blue-400 p-1"><Eye className="h-4 w-4" /></a>
                    <button onClick={() => deleteAttachment(a.id)} className="text-muted-foreground/70 hover:text-red-400 p-1"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'activities' && (
        <div className="space-y-3">
          <div className="bg-muted/50 dark:bg-gray-700/30 rounded-lg p-3 space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <InputField label="Titolo" value={newActivity.title} onChange={(v: string) => setNewActivity(a => ({ ...a, title: v }))} />
              <SelectField label="Tipo" value={newActivity.type} onChange={(v: string) => setNewActivity(a => ({ ...a, type: v }))}
                options={[{ value: 'task', label: 'Task' }, { value: 'call', label: 'Chiamata' }, { value: 'email', label: 'Email' }, { value: 'meeting', label: 'Meeting' }]} />
              <div>
                <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Scadenza</label>
                <input type="date" value={newActivity.due_date} onChange={(e) => setNewActivity(a => ({ ...a, due_date: e.target.value }))}
                  className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm" />
              </div>
              <InputField label="Descrizione" value={newActivity.description} onChange={(v: string) => setNewActivity(a => ({ ...a, description: v }))} />
            </div>
            <button onClick={addActivity} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg font-medium text-sm">Aggiungi Attività</button>
          </div>
          {loadingTab ? <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Caricamento...</p> : activities.length === 0 ? (
            <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm text-center py-4">Nessuna attività</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {activities.map((a) => (
                <div key={a.id} className={`bg-muted dark:bg-gray-700/50 rounded-lg p-3 flex items-center gap-3 ${a.completed ? 'opacity-60' : ''}`}>
                  <button onClick={() => toggleActivity(a.id)}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${a.completed ? 'bg-green-500 border-green-500' : 'border-border dark:border-gray-500'}`}>
                    {a.completed && <Check className="h-3 w-3 text-white" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${a.completed ? 'line-through text-muted-foreground/70' : 'text-foreground dark:text-white'}`}>{a.title}</p>
                    <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">
                      <Badge className="bg-gray-200 dark:bg-gray-600 text-muted-foreground dark:text-muted-foreground/60 text-[10px] mr-1">{a.type}</Badge>
                      {a.due_date && new Date(a.due_date).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                  <button onClick={() => deleteActivity(a.id)} className="text-muted-foreground/70 hover:text-red-400 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'quotes' && (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {(!detail?.linked_quotes || detail.linked_quotes.length === 0) ? (
            <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm text-center py-4">Nessun preventivo collegato</p>
          ) : detail.linked_quotes.map((q) => (
            <div key={q.id || q.quote_number} className="bg-muted dark:bg-gray-700/50 rounded-lg p-3 flex items-center justify-between">
              <div>
                <p className="text-foreground dark:text-white text-sm font-medium">{q.quote_number}</p>
                <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">{q.created_at && new Date(q.created_at).toLocaleDateString('it-IT')}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={q.status === 'accepted' ? 'bg-green-500/20 text-green-400' : q.status === 'sent' ? 'bg-blue-500/20 text-blue-400' : 'bg-muted/500/20 text-muted-foreground/70'}>{q.status}</Badge>
                <span className="text-sm font-bold text-foreground dark:text-white">€ {Number(q.total_gross || '0').toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'contracts' && (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {(!detail?.linked_contracts || detail.linked_contracts.length === 0) ? (
            <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm text-center py-4">Nessun contratto collegato</p>
          ) : detail.linked_contracts.map((c) => (
            <div key={c.id || c.contract_number} className="bg-muted dark:bg-gray-700/50 rounded-lg p-3 flex items-center justify-between">
              <div>
                <p className="text-foreground dark:text-white text-sm font-medium">{c.contract_number}</p>
                <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">{c.created_at && new Date(c.created_at).toLocaleDateString('it-IT')}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={c.status === 'signed' ? 'bg-green-500/20 text-green-400' : c.status === 'active' ? 'bg-blue-500/20 text-blue-400' : 'bg-muted/500/20 text-muted-foreground/70'}>{c.status}</Badge>
                <span className="text-sm font-bold text-foreground dark:text-white">€ {Number(c.total_gross || '0').toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'history' && (
        <div>
          {history.length === 0 ? (
            <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm text-center py-4">Nessun movimento registrato</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {history.map((h) => (
                <div key={h.id} className="bg-muted dark:bg-gray-700/50 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-400" />
                    <span className="text-foreground dark:text-white text-sm">{h.from_stage_name || '—'}</span>
                    <span className="text-muted-foreground/70">→</span>
                    <span className="text-foreground dark:text-white text-sm font-medium">{h.to_stage_name || '—'}</span>
                  </div>
                  <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">
                    {h.changed_at ? new Date(h.changed_at).toLocaleString('it-IT') : ''}
                    {h.changed_by ? ` · ${h.changed_by}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between pt-2 border-t border-border dark:border-gray-700">
        <div className="flex items-center gap-2">
          {onConvert && !entry.customer_id && (
            <button onClick={onConvert}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium text-sm">
              <Users className="h-4 w-4" />Converti in Cliente
            </button>
          )}
          {entry.customer_id && (
            <span className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-green-600 dark:text-green-400 font-medium">
              <CheckCircle className="h-4 w-4" /> Convertito in cliente
            </span>
          )}
          {onDelete && !['won', 'lost', 'archived'].includes(entry.stage_slug || '') && (
            <button onClick={() => { if (window.confirm('Eliminare questa lead dalla pipeline? L\'azione è irreversibile.')) onDelete(); }}
              className="flex items-center gap-2 text-red-500 hover:text-red-400 hover:bg-red-500/10 px-3 py-2 rounded-lg text-sm transition-colors">
              <Trash2 className="h-4 w-4" />Elimina
            </button>
          )}
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white">Annulla</button>
          <button onClick={onSave} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium">
            <Save className="h-4 w-4" />Salva
          </button>
        </div>
      </div>
    </div>
  );
}

